import {
  IcnDoc,
  IcnMail,
  IcnAt,
  IcnUser,
  IcnAlert,
  IcnStopwatch,
  IcnSparkle,
} from '@viquard/doc-ui/icons';
import type { CalendarKindMap } from '../types.js';
import type { CalendarKindTheme } from '../types.js';

/**
 * Default kind theme. Each kind picks a doc-ui icon (no emoji, ever) and a
 * hex color used as the chip's "identity" tone. The CSS layer paints
 * borders + tints using `--vq-cal-kind-tone` (a theme-aware derivative
 * of this hex — see `styles.css`), so consumers don't need light + dark
 * variants per kind.
 *
 * Theme-fitness rules these colors must follow:
 *   1. Mid-saturation, mid-luminance — every hex must hit ≥3:1 against
 *      both the light paper (`#fbfaf6`) and the dark paper (`#16161a`)
 *      after the dark-mode lift defined in styles.css. Near-black or
 *      near-white hexes break dark mode.
 *   2. Distinct hue per kind — readers scan by colour, not by label.
 *
 * Consumers can override on a per-`<CalendarView/>` basis by passing the
 * `kindTheme` prop. Unknown kinds fall back to the `default` entry.
 */
export const DEFAULT_KIND_THEME: CalendarKindMap = {
  publish:    { label: 'Publish',    color: '#b04a1d', icon: IcnDoc },
  newsletter: { label: 'Newsletter', color: '#1f6f8b', icon: IcnMail },
  survey:     { label: 'Survey',     color: '#7a4ec7', icon: IcnSparkle },
  thread:     { label: 'Forum',      color: '#4a7c3c', icon: IcnAt },
  workshop:   { label: 'Workshop',   color: '#c79a1f', icon: IcnUser },
  deadline:   { label: 'Deadline',   color: '#a83d2c', icon: IcnAlert },
  // `booking` was `#1a1a1a` (near-black) — invisible on dark paper. The
  // new slate-blue keeps it neutral relative to the warmer kinds while
  // staying readable across every shipped theme.
  booking:    { label: 'Booking',    color: '#3f4d6a', icon: IcnStopwatch },
  default:    { label: 'Event',      color: '#5a5750', icon: IcnDoc },
};

const FALLBACK_KIND_THEME: CalendarKindTheme = {
  label: 'Event',
  color: '#5a5750',
  icon: IcnDoc,
};

export function resolveKind(map: CalendarKindMap, kind: string): CalendarKindTheme {
  return map[kind] ?? map.default ?? DEFAULT_KIND_THEME.default ?? FALLBACK_KIND_THEME;
}
